import './schedule-later.css';

import EventEmitter from 'eventemitter3';
import { IScheduleLater } from './schedule-later.interface';
import { Util } from '../../../utils/util';
import { Order } from '../../../models/order';
import { Data } from '../../../models/data';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { IDateStartEndTime } from '../../../utils/util.interface';
import { OnlineOrderingService } from '../../../services/online-ordering-service';
import { LimitedTime } from '../../shared/LimitedTimes/limited-time';
import { IName } from '../../../interfaces/global.interfaces';
import { FieldType, OrderType } from '../../../types/enums';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { InputValidator } from '../../../validators/input-validator';
import { DataLayerService } from '../../../services/data-layer-service';

/**
 * Manages the scheduling of an order for a later time.
 */
export class ScheduleLater {
    private readonly _item: IScheduleLater;
    private readonly _containerElement: HTMLElement;
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _onlineOrderingService: OnlineOrderingService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _bindingHelper: TwoWayBindingHelper<IScheduleLater>;
    private readonly _datePickerInput: HTMLInputElement;
    private readonly _timePickerInput: HTMLSelectElement;
    private readonly _limitedTimes: LimitedTime;
    private readonly _idPrefix: string;
    private readonly _onFutureDateSet?: (date: Date, minute: number) => void;
    private readonly _getOrderTypeId: () => OrderType;
    private _restoringMinute: number = -1;

    /**
     * Initializes a new instance of the ScheduleLater class.
     * @param {string} [idPrefix="checkout"] - Prefix for all DOM element IDs, allowing multiple instances without ID conflicts.
     * @param {() => OrderType} [getOrderTypeId] - Optional override to source the order type from somewhere other than the order model (e.g. a pending, not-yet-applied selection). Defaults to reading from `order`.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, onlineOrderingService: OnlineOrderingService, dataLayerService: DataLayerService, idPrefix: string = "checkout", onFutureDateSet?: (date: Date, minute: number) => void, getOrderTypeId?: () => OrderType) {
        this._idPrefix = idPrefix;
        this._onFutureDateSet = onFutureDateSet;
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._onlineOrderingService = onlineOrderingService;
        this._dataLayerService = dataLayerService;
        this._getOrderTypeId = getOrderTypeId ?? (() => this._order.getProperty("OrderTypeID"));
        this._item = this._initializeScheduleModel();
        this._containerElement = this._createHtmlContainer();
        this._datePickerInput = this._containerElement.querySelector(`#txt_${this._idPrefix}_time_later_date`) as HTMLInputElement;
        this._timePickerInput = this._containerElement.querySelector(`#ddl_${this._idPrefix}_time_later_time`) as HTMLSelectElement;
        this._limitedTimes = new LimitedTime(this._eventBus, this._data, this._context, this._localizationService, this._order, this._onlineOrderingService, this._dataLayerService, `div_${this._idPrefix}_time_later_limited_times`, this._getOrderTypeId);
        this._bindingHelper = new TwoWayBindingHelper(this._item);
    }

    /**
     * Gets the HTML container element for the schedule later component.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    /**
     * Clears the timer for limited-time selections.
     */
    public clearLimitedTimesTimer(): void {
        this._limitedTimes.clearTimer();
    }

    /**
     * Creates and initializes the schedule model.
     * @returns The initialized schedule model.
     */
    private _initializeScheduleModel(): IScheduleLater {
        return {
            date: "",
            time: "-1",
            isLimitedTimes: false
        };
    }

    /**
     * Resets the schedule item model to its default state.
     */
    public resetItemModel(): void {
        this._item.date = "";
        this._item.time = "-1";
        this._item.isLimitedTimes = false;
    }

    /**
     * Recomputes date validity and the available time options for the currently selected date,
     * without resetting that date. Callers with a pending, not-yet-applied order type selection
     * (e.g. OrderTypeAndTime, via TimeSection) should invoke this whenever that selection changes,
     * since store hours/wait time can differ by order type.
     */
    public refresh(): void {
        if (!this._data.storeKey)
            return;

        this._setOrderTime();
        this._handleOrderTimeChange();
    }

    /**
     * Resolves the order type subtype for the currently effective order type (see _getOrderTypeId).
     * Returns the order's own cached subtype when it matches the real order type, to avoid any
     * behavior change for callers that don't override the order type.
     */
    private _getOrderTypeSubType(): any {
        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId === this._order.getProperty("OrderTypeID"))
            return this._order.getProperty("OrderTypeSubType");

        return OnlineOrderingUtil.getOrderType(orderTypeId, this._data.getProperty("OrderTypeSubTypes") ?? []) ?? this._order.getProperty("OrderTypeSubType");
    }

    /**
     * Initializes the component asynchronously.
     */
    public init(forceReset?: boolean, initialDate?: Date, initialMinute?: number): void {
        if (!this._data.storeKey)
            return;

        this._bindingHelper.clearListeners();
        this._setupTwoWayBinding();

        const isFirstRender = this._item.time === "-1";
        this._restoringMinute = initialMinute ?? this._order.getProperty("FutureMinute") ?? -1;
        if (this._restoringMinute !== -1)
            this._item.time = this._restoringMinute.toString();

        if (!isFirstRender && !forceReset)
            return;

        this._renderPage(initialDate, initialMinute);
        this._setupEventListeners();
    }

    /**
     * Sets up two-way binding between the UI and the schedule data model.
     */
    private _setupTwoWayBinding(): void {
        this._bindingHelper.bindToInput("date", `txt_${this._idPrefix}_time_later_date`);
        this._bindingHelper.bindToInput("time", `ddl_${this._idPrefix}_time_later_time`);
    }

    /**
     * Clears the timer for limited-time scheduling.
     */
    public clearTimer(): void {
        this._limitedTimes.clearTimer();
    }

    /**
     * Creates and returns the HTML structure for the schedule later UI.
     * @returns The created HTML element.
     */
    private _createHtmlContainer(): HTMLElement {
        const p = this._idPrefix;
        const html = `
            <div>
                <div class="schedule-later-group">
                    <div class="form-group">
                        <label for="txt_${p}_time_later_date" class="required-star" ltag="Date">Date</label>
                        <input id="txt_${p}_time_later_date" type="date" value="" min="2023-1-1" aria-describedby="spn_${p}_time_later_date_error" aria-invalid="false" />
                        <p id="spn_${p}_time_later_date_error" class="error-text hidden" role="alert" aria-live="polite" ltag="NoFutureOnlineForSelectedDate">
                            ${this._localizationService.names("NoFutureOnlineForSelectedDate")}
                        </p>
                    </div>

                    <div id="div_${p}_time_later_time" class="form-group">
                        <label for="ddl_${p}_time_later_time" class="required-star" ltag="Time">Time</label>
                        <select id="ddl_${p}_time_later_time" aria-describedby="spn_${p}_time_later_time_error spn_${p}_time_later_time_error_validation"></select>
                        <p id="spn_${p}_time_later_time_error" class="error-text hidden" role="alert" aria-live="polite" ltag="InvalidTimeNotSelected">
                            ${this._localizationService.names("InvalidTimeNotSelected")}
                        </p>
                        <p id="spn_${p}_time_later_time_error_validation" class="error-text hidden" role="alert" aria-live="polite" ltag="InvalidTimeOutOfRange">
                            ${this._localizationService.names("InvalidTimeOutOfRange")}
                        </p>
                    </div>
                </div>

                <div id="div_${p}_time_later_limited_times"
                     class="limited-time hidden"
                     role="radiogroup"
                     aria-labelledby="p_${p}_time_header"
                     aria-describedby="spn_${p}_time_later_time_error"></div>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Renders the scheduling page.
     */
    private _renderPage(initialDate?: Date, initialMinute?: number): void {
        const businessDate = Util.getNowStore(this._data);
        this._datePickerInput.min = Util.formatDateInput(businessDate);

        if (initialDate && initialMinute !== undefined) {
            this._item.date = Util.formatDateInput(initialDate);
            this._item.time = initialMinute.toString();
        } else {
            this._item.date = Util.formatDateInput(businessDate);
        }

        this._handleDateChange();
    }

    /**
     * Sets up event listeners for date and time inputs.
     */
    private _setupEventListeners(): void {
        Util.setElement("onchange", `txt_${this._idPrefix}_time_later_date`, this._handleDateChange);
        Util.setElement("onchange", `ddl_${this._idPrefix}_time_later_time`, this._handleTimeChange);
    }

    /**
     * Handles the date change event asynchronously.
     */
    private readonly _handleDateChange = (): void => {
        this._setOrderTime();
        this._handleOrderTimeChange();
    }

    /**
     * Handles the time change event by setting the order's future date.
     */
    private readonly _handleTimeChange = (): void => {
        this._updateOrderFutureDate();
    }

    /**
     * Updates the order's future scheduled date based on the selected time.
     */
    private _updateOrderFutureDate(): void {
        const selectedTime = Number(this._item.time);
        if (selectedTime === -1)
            return;

        const date = this._getSelectedOrderDate();
        this._onFutureDateSet?.(date, selectedTime);
    }

    /**
     * Hides all error messages related to scheduling inputs.
     */
    private _hideErrorMessages(): void {
        const p = this._idPrefix;
        Util.hideElementErrors(`txt_${p}_time_later_date`, `spn_${p}_time_later_date_error`);
        Util.hideElementErrors(`ddl_${p}_time_later_time`, `spn_${p}_time_later_time_error`);
        Util.hideElementErrors(`ddl_${p}_time_later_time`, `spn_${p}_time_later_time_error_validation`);
    }

    /**
     * Displays an error message for either the date or time input.
     * @param isDate - Indicates if the error is related to the date input.
     * @param message - The error message to display.
     * @param localizationTag - Optional localization tag for the message.
     * @param localizationParams - Optional localization parameters.
     */
    private _displayErrorMessage(isDate: boolean, message: string, ltag?: string, ltagj?: IName[]): void {
        this._hideErrorMessages();

        const p = this._idPrefix;
        const errorSpanId = isDate ? `spn_${p}_time_later_date_error` : `spn_${p}_time_later_time_error`;
        const inputId = isDate ? `txt_${p}_time_later_date` : `ddl_${p}_time_later_time`;

        const errorSpan = this._containerElement.querySelector(`#${errorSpanId}`) as HTMLElement;
        if (!errorSpan)
            return;

        errorSpan.removeAttribute("ltag");
        errorSpan.removeAttribute("ltagj");
        errorSpan.innerHTML = message;

        if (ltag)
            errorSpan.setAttribute("ltag", ltag);

        if (ltagj)
            errorSpan.setAttribute("ltagj", Util.toLtagj(ltagj));

        InputValidator.validate(FieldType.FAILED, inputId, errorSpanId);
    }

    /**
     * Retrieves the selected date from the scheduling model.
     * @returns The selected date as a JavaScript Date object.
     */
    private _getSelectedDate(): Date {
        const date = this._item.date === "" ? Util.getBusinessDate(this._data) : this._formatISODateToDate(this._item.date);
        return date;
    }

    private _formatISODateToDate(date: string): Date {
        const formattedDate = Util.formatDateFunc(date);
        return new Date(formattedDate);
    }

    /**
     * Retrieves the selected order date with an adjustment for business scheduling minutes (BSM).
     * @returns The adjusted order date.
     */
    private _getSelectedOrderDate(): Date {
        const selectedDate = this._getSelectedDate();
        return Util.DateAdd(selectedDate, this._data.getProperty("Settings").BSM, 'm');
    }

    /**
     * Gets the next available order date based on store availability.
     * @returns The next available date formatted as a string.
     */
    private _getNextAvailableOrderDate(): string {
        const nextOpenDay = Util.getNextOpenDay(this._data);
        const finalDate = (nextOpenDay.Date || Util.getNowStore(this._data));
        return Util.formatDateInput(finalDate);
    }

    /**
     * Sets the order time while validating date availability.
     * @returns A promise resolving to a boolean indicating whether the order time was successfully set.
     */
    private _setOrderTime(): void {
        this._item.time = "-1";
        const selectedDate = this._getSelectedOrderDate();
        const busDate = Util.getBusinessDate(this._data);

        // Ensure selected date is not before the business date
        if (Util.DateDiff("d", selectedDate, busDate) < 0)
            return;

        const orderBusinessDate = this._order.getProperty("BUSDate");
        const dateSelectionInfo = Util.getTodayAndSelectedDateTimes(this._data, this._getOrderTypeSubType(), selectedDate, 0);

        // Handle store closure on holidays
        if (dateSelectionInfo.selectedDay.IsClosedHoliday) {
            const holiday = this._data.getProperty("Holidays").find(x => x.HolidayId === dateSelectionInfo.selectedDay.HolidayID);
            const name = this._localizationService.names("STORE_CLOSED_HOLIDAY").replace("{{holiday_name}}", holiday ? this._localizationService.GetName(holiday.Names) : "");

            // Generate translations for supported languages
            const nameObj: IName[] = this._localizationService.getSupportedLanguages().map(lang => {
                const tempText = this._localizationService.names("STORE_CLOSED_HOLIDAY", lang).replace("{{holiday_name}}", holiday ? this._localizationService.GetName(holiday.Names, lang) : "");
                return { CULT: lang, NAM: tempText };
            });

            this._displayErrorMessage(true, name, undefined, nameObj);

            // No Need to change date: Revert to the last valid order date
            // const orderFutureDate = this._order.getProperty("FutureDate");
            // if (orderFutureDate)
            //     this._item.date = Util.formatDateInput(orderFutureDate);
            // else if (orderBusinessDate)
            //     this._item.date = orderBusinessDate;
            // else
            //     this._item.date = this._getNextAvailableOrderDate();
        } else if (!dateSelectionInfo.selectedDay.FutureOrder) {
            // Handle cases where future ordering is not available
            this._item.date = orderBusinessDate
                ? this._formatToISODate(orderBusinessDate)
                : this._getNextAvailableOrderDate();

            this._displayErrorMessage(true, this._localizationService.names("NoFutureOnlineForSelectedDate"), "NoFutureOnlineForSelectedDate");
        }
    }

    /**
     * Converts a date string from "MM/DD/YYYY" format to "YYYY-MM-DD" (ISO format).
     *
     * @param {string} date - A string representation of a date in "MM/DD/YYYY" format.
     * @returns {string} The date string in "YYYY-MM-DD" format.
     * @throws {Error} If the input format is incorrect.
     */
    private _formatToISODate(date: string): string {
        const datePattern = /^\d{2}\/\d{2}\/\d{4}$/;
        if (!datePattern.test(date))
            throw new Error('Invalid date format. Expected format: MM/DD/YYYY');

        const [month, day, year] = date.split("/");

        return `${year}-${month}-${day}`;
    }

    /**
     * Handles changes in order time selection asynchronously.
     */
    private _handleOrderTimeChange(): void {
        const p = this._idPrefix;
        const orderTypeId = this._getOrderTypeId();
        const busDate = Util.getBusinessDate(this._data);
        const date = this._getOrderDateByOrderType(orderTypeId);
        const dateDifference = Util.DateDiff("d", date, busDate);

        if (dateDifference >= 0) {
            // Check if the selected date is a holiday with restrictions for this order type
            const holiday = Util.CheckHolidays(this._data, date, false, this._data.getProperty("StoreInfo").storeId);
            const isHolidayRestricted = holiday?.Info?.some(info => info.orderTypeId === orderTypeId && info.checked) || false;

            if (isHolidayRestricted) {
                // Display limited order time options
                this._item.time = "-1";
                this._limitedTimes.initAsync(this._getSelectedOrderDate());

                Util.hideElement(`div_${p}_time_later_time`);
                Util.showElement(`div_${p}_time_later_limited_times`);
            } else {
                // Show and render available time list
                Util.showElement(`div_${p}_time_later_time`);
                Util.hideElement(`div_${p}_time_later_limited_times`);

                let adjustedTime = 0;
                if (dateDifference > 0 || Util.DateDiff("d", date, new Date(Util.getNowStore(this._data))) == 0)
                    adjustedTime = 0;
                else
                    adjustedTime = (Util.getNowStore(this._data).getTime() - date.getTime()) / 1000 / 60;

                this._renderOrderTimeElements(date, adjustedTime);
                this._limitedTimes.clearTimer();
            }
        } else {
            this._timePickerInput.innerHTML = "";
            this._renderOrderTimeElements(date, 0);
        }
    }

    /**
     * Gets the appropriate order date based on the selected order type.
     * @param orderTypeId - The ID of the order type.
     * @returns The applicable order date.
     */
    private _getOrderDateByOrderType(orderTypeId: number) {
        const currentOrderDate = this._getSelectedOrderDate();
        // const currentOrderTime = Number(this._item.time);
        // const dateSelectionInfo = Util.getTodayAndSelectedDateTimes(this._data, this._order.getProperty("OrderTypeSubType"), currentOrderDate, currentOrderTime);

        //if (dateSelectionInfo.selectedDay.IsClosedHoliday || !dateSelectionInfo.selectedDay.Online) {
        //    if (dateSelectionInfo.selectedDay.IsToday) {
        //        const otst = OnlineOrderingUtil.getOrderType(orderTypeId, this._data.getProperty("OrderTypeSubTypes"));
        //        const nextOpenDay = Util.getNextOpenDay(this._data, otst);
        //        return nextOpenDay.Date;
        //    } else
        //        return Util.getBusinessDate(this._data);
        //}

        return currentOrderDate;
    }

    /**
     * Renders order time selection elements based on the given date and time.
     * @param selectedDate - The date to render options for.
     * @param selectedTime - The selected time in minutes.
     */
    private _renderOrderTimeElements(selectedDate: Date | null, selectedTime: number): void {
        const p = this._idPrefix;
        const errorSpan = this._containerElement.querySelector(`#spn_${p}_time_later_date_error`) as HTMLElement;
        if (!errorSpan)
            return;

        Util.hideElementErrors(`txt_${p}_time_later_date`, `spn_${p}_time_later_date_error`);
        Util.showElement(`div_${p}_time_later_time`);

        const currentStoreTime = Util.getNowStore(this._data);

        if (selectedDate === null || selectedTime === null) {
            const nextOpenDay = Util.getNextOpenDay(this._data);
            selectedDate = nextOpenDay.Date || currentStoreTime;
            selectedTime = nextOpenDay.Minute;
        }

        const busDate = Util.getBusinessDate(this._data);
        const dateSelectionInfo = Util.getTodayAndSelectedDateTimes(this._data, this._getOrderTypeSubType(), selectedDate, selectedTime);

        if (!dateSelectionInfo.selectedDay.FutureOrder
            || !dateSelectionInfo.selectedDay.Online
            || dateSelectionInfo.selectedDay.IsClosedHoliday
            || Util.DateDiff("d", selectedDate, busDate) < 0) {
            this._displayErrorMessage(true, this._localizationService.names("NoFutureOnlineForSelectedDate"), "NoFutureOnlineForSelectedDate");
            Util.showElement(errorSpan);
            Util.hideElement(`div_${p}_time_later_time`);
            return;
        }

        this._setOrderTimeOptions(dateSelectionInfo.selectedDay);
    }

    /**
     * Populates the time picker dropdown with available order times.
     * @param dateSelectionInfo - Information about available order times for the selected date.
     */
    private _setOrderTimeOptions(dateSelectionInfo: IDateStartEndTime): void {
        const orderTypeSubType = this._getOrderTypeSubType();
        const nowMinutes = Util.getNowStoreMinutes(this._data);

        const storeOpenMinute = this._getNextDivisibleNumber(dateSelectionInfo.StartOrderMinute, 15);
        const storeCloseMinute = dateSelectionInfo.EndOrderMinute;
        const selectedDate = dateSelectionInfo.Date;
        // _setOrderTime() resets _item.time to "-1" during date validation.
        // Fall back to _restoringMinute so a pre-set time is still highlighted.
        const modelTime = Number(this._item.time);
        const selectedTime = modelTime === -1 ? this._restoringMinute : modelTime;
        let selectedIndex = 0;
        let optionIndex = 0;

        // Initialize dropdown options
        this._timePickerInput.innerHTML = `<option value="-1">${this._localizationService.names("SelectTime")}</option>`;

        if (storeOpenMinute < storeCloseMinute) {
            for (let minutes = storeOpenMinute; minutes <= storeCloseMinute; minutes += 15) {
                // Check if current time and wait time is greater than minutes
                if (dateSelectionInfo.IsToday && minutes <= nowMinutes + orderTypeSubType.WaitTime)
                    continue;

                optionIndex += 1;
                const formattedTimeOption = this._formatTimeForDropdown(selectedDate, minutes);
                if (selectedTime == minutes)
                    selectedIndex = optionIndex;

                this._timePickerInput.innerHTML += `<option value="${minutes}">${formattedTimeOption}</option>`;
            }
        }

        this._timePickerInput.selectedIndex = selectedIndex;
        this._item.date = Util.formatDateInput(selectedDate);
    }

    /**
     * Formats a time value into a user-friendly format for dropdown options.
     * @param baseDate - The base date to apply the time to.
     * @param timeInMinutes - The number of minutes since midnight.
     * @returns The formatted time string.
     */
    private _formatTimeForDropdown(baseDate: Date, timeInMinutes: number): string {
        const formattedDate = new Date(baseDate);
        const hours = Math.floor(timeInMinutes / 60);
        const minutes = timeInMinutes % 60;
        formattedDate.setHours(hours, minutes);
        return Util.formatTime(formattedDate, 'en-US');
    }

    /**
     * Gets the next number that is divisible by the given divisor.
     * @param number - The starting number.
     * @param divisor - The divisor to check for divisibility.
     * @returns The next number that is a multiple of the divisor.
     */
    private _getNextDivisibleNumber(number: number, divisor: number): number {
        if (divisor === 0)
            return 0;

        const remainder = number % divisor;
        return remainder === 0 ? number : number + (divisor - remainder);
    }
}
